import { Alert, Spin } from "antd";
import type { Data, Shape } from "plotly.js";
import Plot, { Figure } from "react-plotly.js";
import type { ImbalancePriceInsight } from "../../../services/client";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import { plotlyDateAxis } from "../../../util/formatDate";
import {
  ACTUAL_COLOR,
  GRID_COLOR,
  NOW_COLOR,
  PREDICTION_COLOR,
  QUARTER_HOUR,
  SURFACE_COLOR,
  quarterHourStarts,
  stepLine,
} from "./imbalanceChartLines";
import { spread, type SpreadRow } from "./imbalanceSpread";

const DAY_AHEAD_COLOR = "#389e0d";
const WRONG_SIDE_COLOR = "rgba(245, 34, 45, 0.10)";
const PRICE_DOMAIN: [number, number] = [0.45, 1];
const SPREAD_DOMAIN: [number, number] = [0, 0.37];

const wrongSideShapes = (rows: SpreadRow[]): Partial<Shape>[] =>
  rows.flatMap(({ timestamp, actual, forecast, dayAhead }) => {
    const actualSpread = spread(actual, dayAhead);
    const forecastSpread = spread(forecast, dayAhead);
    if (
      actualSpread === null ||
      forecastSpread === null ||
      Math.sign(actualSpread) === Math.sign(forecastSpread)
    ) {
      return [];
    }
    return [
      {
        type: "rect",
        xref: "x",
        yref: "paper",
        x0: timestamp,
        x1: timestamp + QUARTER_HOUR,
        y0: SPREAD_DOMAIN[0],
        y1: SPREAD_DOMAIN[1],
        fillcolor: WRONG_SIDE_COLOR,
        line: { width: 0 },
        layer: "below",
      },
    ];
  });

type ImbalanceSpreadChartProps = {
  insights: ImbalancePriceInsight[];
  rows: SpreadRow[];
  isLoading: boolean;
  error: Error | null;
};

export const ImbalanceSpreadChart: React.FC<ImbalanceSpreadChartProps> = ({
  insights,
  rows,
  isLoading,
  error,
}) => {
  if (error) {
    return <Alert message="Error" description={error.message} type="error" />;
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-96">
        <Spin />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <NoData message="No imbalance or day-ahead prices available for this range." />
    );
  }

  const x = quarterHourStarts(insights);
  const now = Date.now();
  const data: Data[] = [
    ...stepLine(
      x,
      rows.map((row) => row.dayAhead),
      "Day-ahead",
      DAY_AHEAD_COLOR,
    ),
    ...stepLine(
      x,
      rows.map((row) => row.forecast),
      "Latest forecast",
      PREDICTION_COLOR,
    ),
    ...stepLine(
      x,
      rows.map((row) => row.actual),
      "Actual",
      ACTUAL_COLOR,
    ),
    ...stepLine(
      x,
      rows.map((row) => spread(row.forecast, row.dayAhead)),
      "Forecast − day-ahead",
      PREDICTION_COLOR,
      "y2",
    ),
    ...stepLine(
      x,
      rows.map((row) => spread(row.actual, row.dayAhead)),
      "Actual − day-ahead",
      ACTUAL_COLOR,
      "y2",
    ),
  ];

  const layout: Figure["layout"] = {
    title: "BE imbalance price against day-ahead",
    plot_bgcolor: SURFACE_COLOR,
    paper_bgcolor: SURFACE_COLOR,
    xaxis: {
      ...plotlyDateAxis,
      title: "Time",
      gridcolor: GRID_COLOR,
      anchor: "y2",
    },
    yaxis: {
      title: "€/MWh",
      domain: PRICE_DOMAIN,
      gridcolor: GRID_COLOR,
      zeroline: true,
      zerolinecolor: "#d9d9d9",
    },
    yaxis2: {
      title: "Spread to day-ahead (€/MWh)",
      domain: SPREAD_DOMAIN,
      gridcolor: GRID_COLOR,
      zeroline: true,
      zerolinecolor: "#8c8c8c",
    },
    shapes: [
      ...wrongSideShapes(rows),
      {
        type: "line",
        x0: now,
        x1: now,
        yref: "paper",
        y0: 0,
        y1: 1,
        line: { color: NOW_COLOR, width: 1, dash: "dot" },
      },
    ],
    annotations: [
      {
        x: now,
        yref: "paper",
        y: 1,
        text: "now",
        showarrow: false,
        yanchor: "bottom",
        font: { color: NOW_COLOR },
      },
    ],
    legend: { orientation: "h", traceorder: "reversed" },
    hovermode: "x unified",
    margin: { t: 60, r: 20, b: 50, l: 70 },
  };

  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 640 }}
      config={{ displaylogo: false, responsive: true }}
    />
  );
};
